/**
 * Snowcubes Analytics - PostHog wrapper with dev/prod separation
 */

// Event name constants
const ANALYTICS_EVENTS = {
  ADD_TO_CART: 'add_to_cart',
  ADD_TO_CART_FAILED: 'add_to_cart_failed',
  QUICK_ADD_OPEN: 'quick_add_open',
  CART_QUANTITY_UPDATE: 'cart_quantity_update',
  CART_QUANTITY_UPDATE_FAILED: 'cart_quantity_update_failed',
  NEWSLETTER_SIGNUP: 'newsletter_signup',
  NEWSLETTER_SIGNUP_FAILED: 'newsletter_signup_failed',
  CONTACT_FORM_SUBMIT: 'contact_form_submit',
  VARIANT_SELECT: 'variant_select',
  DELIVERY_DATE_SELECT: 'delivery_date_select',
  FLAVOR_SELECT: 'flavor_select',
  PRODUCT_VIEW: 'product_view',
  CHECKOUT_CLICK: 'checkout_click',
  PRODUCT_CARD_CLICK: 'product_card_click',
  NAVIGATION_CLICKED: 'navigation_clicked',
  SEARCH_RESULTS_VIEWED: 'search_results_viewed',
  SEARCH_RESULT_CLICKED: 'search_result_clicked',
  SEARCH_REQUEST_FAILED: 'search_request_failed',
  PDP_MEDIA_VIEWED: 'pdp_media_viewed',
  PDP_DISCLOSURE_OPENED: 'pdp_disclosure_opened',
  PDP_TRUST_CUE_CLICKED: 'pdp_trust_cue_clicked',
  CART_VIEW: 'cart_view',
  CONTINUE_SHOPPING_CLICK: 'continue_shopping_click',
  HOMEPAGE_VIEW: 'homepage_view',
  GIFT_HELP_OPENED: 'gift_help_opened',
  GIFT_HELP_RESULT: 'gift_help_result',
  GIFT_HELP_PICK_SELECTED: 'gift_help_pick_selected',
  GIFT_HELP_ADD_RESULT: 'gift_help_add_result',
  GIFT_CUSTOMIZER_OPENED: 'gift_customizer_opened',
  GIFT_CUSTOMIZER_STEP_VIEWED: 'gift_customizer_step_viewed',
  GIFT_CUSTOMIZER_COMPLETED: 'gift_customizer_completed',
  GIFT_CUSTOMIZER_FAILED: 'gift_customizer_failed',
  GIFT_CUSTOMIZER_ABANDONED: 'gift_customizer_abandoned',
  GIFT_OPTION_SELECTED: 'gift_option_selected',
  GIFT_PROMO_VIEWED: 'gift_promo_viewed',
  GIFT_PROMO_CLICKED: 'gift_promo_clicked',
  // Wedding favors funnel — see ai/plans/wedding-favors-2026/PAGE-REBUILD.md
  WEDDING_PAGE_VIEWED: 'wedding_page_viewed',
  WEDDING_CTA_CLICKED: 'wedding_cta_clicked',
  WEDDING_INQUIRY_SUBMITTED: 'wedding_inquiry_submitted',
  WEDDING_BOOKED: 'wedding_booked',
};

// Environment constants
const ENVIRONMENTS = {
  DEVELOPMENT: 'development',
  PRODUCTION: 'production',
};

const CHECKOUT_HANDLER_GLOBAL = '__snowcubesAnalyticsCheckoutHandler';
const PDP_DISCLOSURE_HANDLER_GLOBAL = '__snowcubesAnalyticsPdpDisclosureHandler';
const PDP_TRUST_CUE_HANDLER_GLOBAL = '__snowcubesAnalyticsPdpTrustCueHandler';
const PRODUCT_CARD_HANDLER_GLOBAL = '__snowcubesAnalyticsProductCardHandler';
const NAVIGATION_HANDLER_GLOBAL = '__snowcubesAnalyticsNavigationHandler';
const CART_VIEW_HANDLER_GLOBAL = '__snowcubesAnalyticsCartViewHandler';
const CART_VIEW_RECEIPT_GLOBAL = '__snowcubesAnalyticsCartViewReceipt';
const CART_PAGE_VIEW_GLOBAL = '__snowcubesAnalyticsCartPageViewTracked';
const GIFT_PROMO_HANDLER_GLOBAL = '__snowcubesAnalyticsGiftPromoHandler';
const GIFT_PROMO_STATE_GLOBAL = '__snowcubesAnalyticsGiftPromoState';
const GIFT_PROMO_VIEWED_GLOBAL = '__snowcubesAnalyticsGiftPromoViewed';
const GIFT_FLOW_VIEWED_GLOBAL = '__snowcubesAnalyticsGiftFlowViewed';
const GIFT_FLOW_VIEWED_CTA_GLOBAL = '__snowcubesAnalyticsGiftFlowViewedCtas';
const PRODUCT_ANALYTICS_SELECTOR = '[data-snowcubes-product-analytics]';
const PRODUCT_CARD_SELECTOR = '[data-product-card-analytics]';
const NAVIGATION_CONTAINER_SELECTOR = '[data-navigation-location]';
const CHECKOUT_SELECTOR = '[data-delivery-checkout-button]';
const CART_ANALYTICS_SELECTOR = '[data-cart-analytics]';
const CART_DRAWER_SELECTOR = 'details[id^="dialog-trigger-cart-"]';
const GIFT_PROMO_SELECTOR = '[data-gift-promo="homepage"]';
const GIFT_PROMO_CTA_SELECTOR = '[data-gift-promo-cta]';
const GIFT_FLOW_CTA_SELECTOR = '[data-gift-flow-entry]';
const GIFT_FLOW_ENTRIES = new Set(['primary', 'band']);
const CART_VIEW_DEDUPE_MS = 500;
const UTM_STORAGE_KEY = 'snowcubes_utm_first_touch_v1';
const UTM_VALUE_MAX_LENGTH = 120;
const UTM_KEYS = Object.freeze([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
]);
const ANALYTICS_CONTEXT_GLOBAL = '__SC_ANALYTICS_CONTEXT';
const POSTHOG_READY_EVENT = 'snowcubes:posthog-ready';
const POSTHOG_READY_HANDLER_GLOBAL = '__snowcubesAnalyticsPostHogReadyHandler';
const POSTHOG_PAGE_VIEW_GLOBAL = '__scPostHogPageViewTracked';
const POSTHOG_HOMEPAGE_VIEW_GLOBAL = '__scPostHogHomepageViewTracked';
const POSTHOG_OWNED_MARKER = '__SC_POSTHOG_OWNED';
// `scripts/prepare-release-theme.mjs` replaces this token only in an isolated
// Shopify staging directory. The source checkout stays fail-closed and never
// guesses a Git revision from a theme ID or CDN asset version.
const BUILD_RELEASE_SHA = '__SC_RELEASE_SHA__';
const SAFE_CONTEXT_VALUE = /^[a-z0-9_-]+$/i;
const SAFE_RELEASE_SHA = /^[a-f0-9]{7,40}$/i;

let sessionAttributionCache = null;
let postHogRuntimeInitialized = false;

// Helper: Check if running in browser
function isClient() {
  return typeof window !== 'undefined';
}

// Helper: Check if PostHog is loaded AND consent-gated by our own loader.
//
// The readiness signal must live on `window`, not on `window.posthog`. The
// consent loader stamps POSTHOG_OWNED_MARKER onto the array stub the PostHog
// snippet creates, and roughly 250ms later `array.js` REPLACES `window.posthog`
// wholesale with the real SDK — taking the marker with it. Gating on that marker
// therefore passed only for the first few hundred milliseconds of a page and
// failed silently forever after, so load-time events ($pageview, product_view)
// were recorded while every user-interaction event (add_to_cart, checkout_click,
// variant_select) was dropped. Measured live 2026-07-31: marker=true at 250ms,
// null at 501ms once the SDK finished loading.
//
// __SC_POSTHOG_READY is owned by the same loader, survives the SDK swap because
// it hangs off `window`, and is set back to false on consent revocation — so
// this keeps the consent gate exactly as strict while surviving the swap.
// The marker is still accepted as a fallback for the pre-swap window.
function hasPostHog() {
  if (!isClient()) return false;
  const consentReady =
    window.__SC_POSTHOG_READY === true ||
    window.posthog?.[POSTHOG_OWNED_MARKER] === true;
  return consentReady && typeof window.posthog?.capture === 'function';
}

function capturePostHogEvent(eventName, payload) {
  if (!hasPostHog()) return false;

  try {
    window.posthog.capture(eventName, payload);
    if (eventName === '$pageview') {
      window[POSTHOG_PAGE_VIEW_GLOBAL] = true;
    }
    if (eventName === ANALYTICS_EVENTS.HOMEPAGE_VIEW) {
      window[POSTHOG_HOMEPAGE_VIEW_GLOBAL] = true;
    }
  } catch (error) {
    if (typeof window.posthog?.captureException === 'function') {
      try {
        window.posthog.captureException(error);
      } catch (_captureError) {
        // ignore - best effort only
      }
    }
    // Silently fail - analytics should never break the app
    return false;
  }
  return true;
}

/**
 * Detect environment based on hostname
 * Dev: *.myshopify.com, localhost, 127.0.0.1
 * Prod: Everything else (your actual domain)
 */
function getEnvironment() {
  if (!isClient()) return ENVIRONMENTS.PRODUCTION;

  const hostname = window.location.hostname;

  if (
    hostname.includes('.myshopify.com') ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.includes('ngrok')
  ) {
    return ENVIRONMENTS.DEVELOPMENT;
  }

  return ENVIRONMENTS.PRODUCTION;
}

function getPathname() {
  if (!isClient()) return '/';

  const pathname = window.location.pathname;
  return pathname || '/';
}

function normalizeContextValue(value, maxLength = 80) {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const normalized = String(value).trim().slice(0, maxLength);
  return SAFE_CONTEXT_VALUE.test(normalized) ? normalized : '';
}

function normalizeLabelValue(value, maxLength = 80) {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  return String(value)
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);
}

function getAnalyticsAssetVersion() {
  if (!isClient() || !document?.querySelector) return '';

  const script = document.querySelector('script[src*="/assets/analytics.js"]');
  const src = script?.getAttribute?.('src');
  if (!src) return '';

  try {
    return normalizeContextValue(
      new URL(src, window.location.href).searchParams.get('v'),
      80
    );
  } catch (_error) {
    return '';
  }
}

/**
 * Canonical deploy/page context. A source SHA is included only when a deploy
 * pipeline explicitly injects one; it is never guessed from a theme asset.
 */
function getRuntimeContext() {
  if (!isClient()) return {};

  const injected = window[ANALYTICS_CONTEXT_GLOBAL];
  const candidate = injected && typeof injected === 'object' ? injected : {};
  const shopifyTheme = window.Shopify?.theme || {};
  const contextReleaseSha =
    typeof candidate.release_sha === 'string' &&
    SAFE_RELEASE_SHA.test(candidate.release_sha.trim())
      ? candidate.release_sha.trim().toLowerCase()
      : '';
  const buildReleaseSha =
    BUILD_RELEASE_SHA.length === 40 && SAFE_RELEASE_SHA.test(BUILD_RELEASE_SHA)
      ? BUILD_RELEASE_SHA.toLowerCase()
      : '';
  const context = {
    page_type: normalizeContextValue(candidate.page_type, 40),
    template_name: normalizeContextValue(candidate.template_name, 40),
    theme_id: normalizeContextValue(candidate.theme_id || shopifyTheme.id, 30),
    theme_role: normalizeContextValue(candidate.theme_role || shopifyTheme.role, 30),
    analytics_asset_version: getAnalyticsAssetVersion(),
    // A verified staging build is canonical; the explicit context fallback is
    // retained for preview/test injection when no release build token exists.
    release_sha: buildReleaseSha || contextReleaseSha,
  };

  return Object.fromEntries(
    Object.entries(context).filter(([, value]) => value !== '')
  );
}

function getReferrerHost() {
  if (!isClient() || !document.referrer) return 'direct';

  try {
    return new URL(document.referrer).host || 'direct';
  } catch (_error) {
    return 'direct';
  }
}

function normalizeUtmValue(value) {
  if (typeof value !== 'string') return '';

  return value
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, UTM_VALUE_MAX_LENGTH);
}

function sanitizeAttribution(candidate) {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return {};
  }

  return UTM_KEYS.reduce((attribution, key) => {
    const value = normalizeUtmValue(candidate[key]);
    if (value) attribution[key] = value;
    return attribution;
  }, {});
}

function parseCurrentAttribution() {
  if (!isClient()) return {};

  try {
    const params = new URLSearchParams(window.location.search || '');
    return sanitizeAttribution(
      UTM_KEYS.reduce((candidate, key) => {
        candidate[key] = params.get(key);
        return candidate;
      }, {})
    );
  } catch (_error) {
    return {};
  }
}

/**
 * Capture the first allowlisted UTM touch in this browser tab. The stored
 * payload contains no URL, referrer, customer, cart, or order data.
 */
function getSessionAttribution() {
  if (!isClient()) return {};
  if (sessionAttributionCache !== null) {
    return { ...sessionAttributionCache };
  }

  try {
    const stored = window.sessionStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      const attribution = sanitizeAttribution(JSON.parse(stored));
      if (Object.keys(attribution).length > 0) {
        sessionAttributionCache = attribution;
        return { ...sessionAttributionCache };
      }
    }
  } catch (_error) {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }

  sessionAttributionCache = parseCurrentAttribution();

  if (Object.keys(sessionAttributionCache).length > 0) {
    try {
      window.sessionStorage.setItem(
        UTM_STORAGE_KEY,
        JSON.stringify(sessionAttributionCache)
      );
    } catch (_error) {
      // Keep the current-page attribution in memory without breaking the app.
    }
  }

  return { ...sessionAttributionCache };
}

/**
 * Track an event with automatic environment tagging
 * @param {string} eventName - Event name from ANALYTICS_EVENTS
 * @param {Object} properties - Additional event properties
 */
function track(eventName, properties = {}) {
  const eventProps = {
    ...properties,
    ...getSessionAttribution(),
    ...getRuntimeContext(),
    page_path: getPathname(),
    environment: getEnvironment(),
  };

  return capturePostHogEvent(eventName, eventProps);
}

/**
 * Track product-related events with standardized properties
 * @param {string} eventName - Event name
 * @param {Object} productData - Product information
 */
function trackProduct(eventName, productData = {}) {
  const source = normalizeContextValue(productData.source, 40);
  const position = readNumericData(productData.position);
  const selectedFlavors = Array.isArray(productData.selectedFlavors)
    ? productData.selectedFlavors
        .map((flavor) => normalizeLabelValue(flavor, 40))
        .filter(Boolean)
        .slice(0, 4)
    : [];

  return track(eventName, {
    product_id: productData.id ?? null,
    product_title: productData.title ?? null,
    product_price: productData.price ?? null,
    product_variant_id: productData.variantId ?? null,
    product_variant_title: productData.variantTitle ?? null,
    quantity: productData.quantity ?? 1,
    ...(source ? { product_source: source } : {}),
    ...(position !== null ? { product_position: position } : {}),
    ...(selectedFlavors.length
      ? {
          selected_flavors: selectedFlavors,
          flavor_selection_count: selectedFlavors.length,
        }
      : {}),
    ...(Number.isFinite(productData.latencyMs)
      ? { latency_ms: productData.latencyMs }
      : {}),
    ...(typeof productData.isGiftWrapOption === 'boolean'
      ? { is_gift_wrap_option: productData.isGiftWrapOption }
      : {}),
    ...(typeof productData.reviewTrustCueAvailable === 'boolean'
      ? {
          review_trust_cue_available:
            productData.reviewTrustCueAvailable,
        }
      : {}),
  });
}

function readNumericData(value) {
  if (value === '' || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Capture the canonical PDP impression once from Liquid-rendered product data.
 * Quick-view/modal markup does not emit this payload.
 */
function captureProductView(root = document) {
  if (!isClient() || !root?.querySelector) return false;

  const payloadNode = root.querySelector(PRODUCT_ANALYTICS_SELECTOR);
  if (!payloadNode || payloadNode.dataset.analyticsCaptured === 'true') return false;

  let productData;
  try {
    productData = JSON.parse(payloadNode.textContent || '{}');
  } catch (_error) {
    return false;
  }

  if (!productData?.id) return false;

  payloadNode.dataset.analyticsCaptured = 'true';
  const captured = trackProduct(ANALYTICS_EVENTS.PRODUCT_VIEW, productData);
  if (captured) {
    payloadNode.dataset.posthogAnalyticsCaptured = 'true';
  }
  return true;
}

function capturePostHogProductView(root = document) {
  if (!hasPostHog() || !root?.querySelector) return false;

  const payloadNode = root.querySelector(PRODUCT_ANALYTICS_SELECTOR);
  if (!payloadNode || payloadNode.dataset.posthogAnalyticsCaptured === 'true') {
    return false;
  }

  let productData;
  try {
    productData = JSON.parse(payloadNode.textContent || '{}');
  } catch (_error) {
    return false;
  }
  if (!productData?.id) return false;

  const captured = capturePostHogEvent(ANALYTICS_EVENTS.PRODUCT_VIEW, {
    product_id: productData.id ?? null,
    product_title: productData.title ?? null,
    product_price: productData.price ?? null,
    product_variant_id: productData.variantId ?? null,
    product_variant_title: productData.variantTitle ?? null,
    quantity: productData.quantity ?? 1,
    ...(typeof productData.reviewTrustCueAvailable === 'boolean'
      ? {
          review_trust_cue_available:
            productData.reviewTrustCueAvailable,
        }
      : {}),
    environment: getEnvironment(),
    ...getSessionAttribution(),
    ...getRuntimeContext(),
  });
  if (!captured) return false;

  payloadNode.dataset.posthogAnalyticsCaptured = 'true';
  return true;
}

function capturePostHogHomepageView() {
  if (
    !hasPostHog() ||
    getPathname() !== '/' ||
    document?.readyState === 'loading' ||
    window[POSTHOG_HOMEPAGE_VIEW_GLOBAL]
  ) {
    return false;
  }

  return capturePostHogEvent(ANALYTICS_EVENTS.HOMEPAGE_VIEW, {
    environment: getEnvironment(),
    ...getSessionAttribution(),
    ...getRuntimeContext(),
  });
}

function captureCanonicalPageView() {
  if (!hasPostHog() || window[POSTHOG_PAGE_VIEW_GLOBAL]) return false;

  return capturePostHogEvent('$pageview', {
    environment: getEnvironment(),
    ...getSessionAttribution(),
    ...getRuntimeContext(),
  });
}

/**
 * Track main checkout intent through delegation so refreshed cart-drawer
 * sections remain covered. A delivery-calendar rejection calls preventDefault
 * on the button before the event bubbles here, so blocked attempts are skipped.
 */
function bindCheckoutTracking() {
  if (!isClient() || !document?.body) return;

  const previousHandler = window[CHECKOUT_HANDLER_GLOBAL];
  if (typeof previousHandler === 'function') {
    document.body.removeEventListener('click', previousHandler);
  }

  const handler = (event) => {
    const checkoutButton = event.target?.closest?.(CHECKOUT_SELECTOR);
    if (!checkoutButton || event.defaultPrevented || checkoutButton.disabled) return;

    track(ANALYTICS_EVENTS.CHECKOUT_CLICK, {
      cart_total: readNumericData(checkoutButton.dataset.cartTotal),
      item_count: readNumericData(checkoutButton.dataset.itemCount),
      checkout_surface: getPathname() === '/cart' ? 'cart_page' : 'cart_drawer',
    });
  };

  document.body.addEventListener('click', handler);
  window[CHECKOUT_HANDLER_GLOBAL] = handler;
}

function bindProductCardTracking() {
  if (!isClient() || !document?.body) return;

  const previousHandler = window[PRODUCT_CARD_HANDLER_GLOBAL];
  if (typeof previousHandler === 'function') {
    document.body.removeEventListener('click', previousHandler);
  }

  const handler = (event) => {
    const card = event.target?.closest?.(PRODUCT_CARD_SELECTOR);
    if (!card) return;

    trackProduct(ANALYTICS_EVENTS.PRODUCT_CARD_CLICK, {
      id: card.dataset.productId || null,
      title: card.dataset.productTitle || null,
      price: readNumericData(card.dataset.productPrice),
      source: card.dataset.productSource || '',
      position: readNumericData(card.dataset.productPosition),
    });
  };

  document.body.addEventListener('click', handler);
  window[PRODUCT_CARD_HANDLER_GLOBAL] = handler;
}

function bindNavigationTracking() {
  if (!isClient() || !document?.body) return;

  const previousHandler = window[NAVIGATION_HANDLER_GLOBAL];
  if (typeof previousHandler === 'function') {
    document.body.removeEventListener('click', previousHandler);
  }

  const handler = (event) => {
    const link = event.target?.closest?.('a');
    const container = link?.closest?.(NAVIGATION_CONTAINER_SELECTOR);
    if (!link || !container) return;

    const location = normalizeContextValue(container.dataset.navigationLocation, 40);
    if (!location) return;

    const links = Array.from(container.querySelectorAll('a'));
    const position = links.indexOf(link);

    track(ANALYTICS_EVENTS.NAVIGATION_CLICKED, {
      navigation_location: location,
      ...(position >= 0 ? { navigation_position: position + 1 } : {}),
    });
  };

  document.body.addEventListener('click', handler);
  window[NAVIGATION_HANDLER_GLOBAL] = handler;
}

function isElementInViewport(element) {
  if (!element?.getBoundingClientRect) return false;

  const rect = element.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < viewportHeight &&
    rect.left < viewportWidth
  );
}

function getEligibleGiftFlowCtas(root = document) {
  if (!root?.querySelectorAll) return [];

  return Array.from(root.querySelectorAll(GIFT_FLOW_CTA_SELECTOR)).filter((cta) =>
    GIFT_FLOW_ENTRIES.has(cta?.dataset?.giftFlowEntry)
  );
}

function getGiftFlowViewedCtas() {
  if (!isClient()) return Object.create(null);

  const existing = window[GIFT_FLOW_VIEWED_CTA_GLOBAL];
  if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
    return existing;
  }

  const viewed = Object.create(null);
  window[GIFT_FLOW_VIEWED_CTA_GLOBAL] = viewed;
  return viewed;
}

function hasUnviewedGiftFlowCta(root = document) {
  const viewed = getGiftFlowViewedCtas();
  return getEligibleGiftFlowCtas(root).some((cta) => {
    const entry = cta?.dataset?.giftFlowEntry;
    return Boolean(entry) && !viewed[entry] && cta.dataset.giftFlowViewed !== 'true';
  });
}

function captureGiftPromoView(root = document) {
  if (!isClient() || !root?.querySelectorAll || window[GIFT_PROMO_VIEWED_GLOBAL]) {
    return false;
  }

  const promo = Array.from(root.querySelectorAll(GIFT_PROMO_SELECTOR)).find(
    isElementInViewport
  );
  if (!promo) return false;

  const captured = track(ANALYTICS_EVENTS.GIFT_PROMO_VIEWED, {
    gift_surface: 'homepage_promo',
  });
  if (!captured) return false;

  promo.dataset.giftPromoViewed = 'true';
  window[GIFT_PROMO_VIEWED_GLOBAL] = true;
  return true;
}

/**
 * Record one consent-gated view per approved gifting-page CTA when it enters
 * the viewport. Clicks already carry the CTA enum; per-entry impressions keep
 * those denominators comparable without sending URLs or copy.
 */
function captureGiftFlowView(root = document) {
  if (!isClient() || !root?.querySelectorAll) return false;

  const viewed = getGiftFlowViewedCtas();
  let capturedAny = false;

  getEligibleGiftFlowCtas(root).forEach((cta) => {
    const entry = cta?.dataset?.giftFlowEntry;
    if (
      !entry ||
      viewed[entry] ||
      cta.dataset.giftFlowViewed === 'true' ||
      !isElementInViewport(cta)
    ) {
      return;
    }

    const captured = track(ANALYTICS_EVENTS.GIFT_PROMO_VIEWED, {
      gift_surface: 'gifting_page',
      gift_cta: entry,
    });
    if (!captured) return;

    cta.dataset.giftFlowViewed = 'true';
    viewed[entry] = true;
    capturedAny = true;
  });

  if (capturedAny) window[GIFT_FLOW_VIEWED_GLOBAL] = true;
  return capturedAny;
}

function bindGiftPromoTracking() {
  if (!isClient() || !document?.body) return;

  const previousState = window[GIFT_PROMO_STATE_GLOBAL];
  if (previousState?.observer?.disconnect) previousState.observer.disconnect();
  if (typeof previousState?.retryHandler === 'function') {
    window.removeEventListener('scroll', previousState.retryHandler);
    window.removeEventListener('resize', previousState.retryHandler);
  }

  const previousHandler = window[GIFT_PROMO_HANDLER_GLOBAL];
  if (typeof previousHandler === 'function') {
    document.body.removeEventListener('click', previousHandler);
  }

  const handler = (event) => {
    const cta = event.target?.closest?.(GIFT_PROMO_CTA_SELECTOR);
    const promo = cta?.closest?.(GIFT_PROMO_SELECTOR);
    if (cta && promo) {
      captureGiftPromoView(document);
      track(ANALYTICS_EVENTS.GIFT_PROMO_CLICKED, {
        gift_surface: 'homepage_promo',
        gift_cta: cta.dataset.giftPromoCta === 'collection'
          ? 'collection'
          : 'unknown',
      });
      return;
    }

    const giftFlowCta = event.target?.closest?.(GIFT_FLOW_CTA_SELECTOR);
    const giftFlowEntry = giftFlowCta?.dataset?.giftFlowEntry;
    if (!giftFlowCta || !GIFT_FLOW_ENTRIES.has(giftFlowEntry)) return;

    // Reuse the existing gift-promo click event for the gifting-page entry
    // points. The fixed enum keeps the payload useful without sending the
    // deep-link URL, CTA copy, catalog data, or any form/customer input.
    captureGiftFlowView(document);
    track(ANALYTICS_EVENTS.GIFT_PROMO_CLICKED, {
      gift_surface: 'gifting_page',
      gift_cta: giftFlowEntry,
    });
  };

  document.body.addEventListener('click', handler);
  window[GIFT_PROMO_HANDLER_GLOBAL] = handler;

  const retryHandler = () => {
    captureGiftPromoView(document);
    captureGiftFlowView(document);

    const waitingForHomepage =
      !window[GIFT_PROMO_VIEWED_GLOBAL] &&
      document.querySelectorAll(GIFT_PROMO_SELECTOR).length > 0;
    const waitingForGiftFlow = hasUnviewedGiftFlowCta(document);
    if (waitingForHomepage || waitingForGiftFlow) return;

    window.removeEventListener('scroll', retryHandler);
    window.removeEventListener('resize', retryHandler);
    const state = window[GIFT_PROMO_STATE_GLOBAL];
    if (state?.observer?.disconnect) state.observer.disconnect();
  };

  const state = { observer: null, retryHandler };
  const promos = Array.from(document.querySelectorAll(GIFT_PROMO_SELECTOR));
  const giftFlowCtas = getEligibleGiftFlowCtas(document);
  const observeTargets = promos.concat(giftFlowCtas);
  if (typeof window.IntersectionObserver === 'function' && observeTargets.length > 0) {
    state.observer = new window.IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) {
        return;
      }
      retryHandler();
    }, { threshold: 0.25 });
    observeTargets.forEach((target) => state.observer.observe(target));
  } else {
    window.addEventListener('scroll', retryHandler, { passive: true });
    window.addEventListener('resize', retryHandler, { passive: true });
  }

  window[GIFT_PROMO_STATE_GLOBAL] = state;
  retryHandler();
}

function bindPdpDisclosureTracking() {
  if (!isClient() || !document?.addEventListener) return;

  const previousHandler = window[PDP_DISCLOSURE_HANDLER_GLOBAL];
  if (typeof previousHandler === 'function') {
    document.removeEventListener('toggle', previousHandler, true);
  }

  const handler = (event) => {
    const disclosure = event.target;
    const disclosureType = disclosure?.dataset?.pdpDisclosureType;
    if (!disclosureType || !disclosure.open) return;

    track(ANALYTICS_EVENTS.PDP_DISCLOSURE_OPENED, {
      disclosure_type: disclosureType,
    });
  };

  document.addEventListener('toggle', handler, true);
  window[PDP_DISCLOSURE_HANDLER_GLOBAL] = handler;
}

function bindPdpTrustCueTracking() {
  if (!isClient() || !document?.body) return;

  const previousHandler = window[PDP_TRUST_CUE_HANDLER_GLOBAL];
  if (typeof previousHandler === 'function') {
    document.body.removeEventListener('click', previousHandler);
  }

  const handler = (event) => {
    const cue = event.target?.closest?.('[data-pdp-trust-cue]');
    if (cue?.dataset?.pdpTrustCue !== 'reviews') return;

    const productId = readNumericData(cue.dataset.productId);
    if (productId === null || productId <= 0) return;

    track(ANALYTICS_EVENTS.PDP_TRUST_CUE_CLICKED, {
      trust_cue: 'reviews',
      product_id: productId,
    });
  };

  document.body.addEventListener('click', handler);
  window[PDP_TRUST_CUE_HANDLER_GLOBAL] = handler;
}

function readCartViewPayload(root, cartSurface) {
  if (!root?.dataset) return null;

  const itemCount = readNumericData(root.dataset.itemCount);
  const lineCount = readNumericData(root.dataset.lineCount);
  const cartTotal = readNumericData(root.dataset.cartTotal);
  const giftLineCount = readNumericData(root.dataset.giftLineCount);
  const giftItemCount = readNumericData(root.dataset.giftItemCount);
  const configuredGiftLineCount = readNumericData(
    root.dataset.configuredGiftLineCount
  );
  const giftConfigurationState = normalizeContextValue(
    root.dataset.giftConfigurationState,
    20
  );

  if (
    itemCount === null ||
    lineCount === null ||
    cartTotal === null ||
    giftLineCount === null ||
    giftItemCount === null ||
    configuredGiftLineCount === null ||
    !['none', 'complete', 'partial', 'missing'].includes(giftConfigurationState)
  ) {
    return null;
  }

  return {
    cart_surface: cartSurface,
    item_count: itemCount,
    line_count: lineCount,
    cart_total: cartTotal,
    has_gift: giftLineCount > 0,
    gift_line_count: giftLineCount,
    gift_item_count: giftItemCount,
    configured_gift_line_count: configuredGiftLineCount,
    gift_configuration_state: giftConfigurationState,
  };
}

function captureCartView(root, cartSurface) {
  const payload = readCartViewPayload(root, cartSurface);
  if (!payload) return false;

  const now = Date.now();
  const signature = JSON.stringify(payload);
  const previous = window[CART_VIEW_RECEIPT_GLOBAL];
  if (
    previous?.signature === signature &&
    now - previous.timestamp < CART_VIEW_DEDUPE_MS
  ) {
    return false;
  }

  // Only advance the dedupe receipt after PostHog accepted the event. If
  // consent/SDK readiness is still pending (or capture throws), the cart may
  // remain visible with no later toggle to trigger a retry. Recording the
  // receipt first would permanently hide that cart_view from the funnel.
  if (!track(ANALYTICS_EVENTS.CART_VIEW, payload)) return false;

  window[CART_VIEW_RECEIPT_GLOBAL] = { signature, timestamp: now };
  return true;
}

/**
 * Measure actual cart visibility, not section refreshes. The persistent header
 * details shells survive cart body replacement, so delegated toggle handling
 * remains valid after add, quantity, and gift-edit rerenders.
 */
function bindCartViewTracking() {
  if (!isClient() || !document?.addEventListener) return;

  const previousHandler = window[CART_VIEW_HANDLER_GLOBAL];
  if (typeof previousHandler === 'function') {
    document.removeEventListener('toggle', previousHandler, true);
  }

  const handler = (event) => {
    const drawer = event.target;
    if (!drawer?.matches?.(CART_DRAWER_SELECTOR) || !drawer.open) return;

    captureCartView(
      drawer.querySelector(CART_ANALYTICS_SELECTOR),
      'cart_drawer'
    );
  };

  document.addEventListener('toggle', handler, true);
  window[CART_VIEW_HANDLER_GLOBAL] = handler;

  if (getPathname() === '/cart' && !window[CART_PAGE_VIEW_GLOBAL]) {
    const cartPage = document.querySelector(
      '#Main-cart-items [data-cart-analytics]'
    );
    if (cartPage && captureCartView(cartPage, 'cart_page')) {
      window[CART_PAGE_VIEW_GLOBAL] = true;
    }
  }
}

/**
 * Initialize analytics - register environment as super property
 */
function initializePostHogRuntime({ captureCurrentPage = false } = {}) {
  if (!hasPostHog() || postHogRuntimeInitialized) return false;

  const attribution = getSessionAttribution();
  const runtimeContext = getRuntimeContext();
  let canonicalContextRegistered = false;

  try {
    const env = getEnvironment();
    window.posthog.register({
      environment: env,
      ...runtimeContext,
    });

    if (
      Object.keys(attribution).length > 0 &&
      typeof window.posthog.register_for_session === 'function'
    ) {
      window.posthog.register_for_session(attribution);
    }
    canonicalContextRegistered = true;
  } catch (error) {
    // Silently fail - analytics should never break the app
    // Keep initialization retryable so a transient SDK/register failure does
    // not permanently suppress the canonical pageview/context path.
    postHogRuntimeInitialized = false;
    return false;
  }

  postHogRuntimeInitialized = true;
  if (captureCurrentPage && canonicalContextRegistered) {
    captureCanonicalPageView();
  }
  const productNode = document?.querySelector?.(PRODUCT_ANALYTICS_SELECTOR);
  if (
    captureCurrentPage &&
    productNode &&
    productNode.dataset.analyticsCaptured !== 'true'
  ) {
    captureProductView();
  } else {
    capturePostHogProductView();
  }
  if (captureCurrentPage && !productNode) {
    capturePostHogHomepageView();
  }
  return true;
}

function handlePostHogReady() {
  const initialized = initializePostHogRuntime({ captureCurrentPage: true });
  // If the first delayed-consent product dispatch throws, retry the same
  // canonical payload once through the existing idempotent latch. A successful
  // first dispatch makes this a no-op.
  capturePostHogProductView();
  // A homepage capture can fail after runtime initialization has completed;
  // the global latch stays unset on failure, so this guarded retry is likewise
  // a no-op after the first successful dispatch.
  if (!document?.querySelector?.(PRODUCT_ANALYTICS_SELECTOR)) {
    capturePostHogHomepageView();
  }
  captureGiftPromoView();
  captureGiftFlowView();
  // Reuse the existing viewport retry boundary once after readiness. If a
  // transient capture failed while the promo was already visible, this gives
  // the same page a second idempotent attempt without buffering or timers.
  const giftPromoRetry = window[GIFT_PROMO_STATE_GLOBAL]?.retryHandler;
  if (typeof giftPromoRetry === 'function') giftPromoRetry();
  // A cart page can initialize before consent/SDK readiness. Rebind and
  // re-read its current aggregate after the consent event so a failed first
  // attempt does not leave the cart-page denominator permanently empty.
  bindCartViewTracking();
  return initialized;
}

function init() {
  captureProductView();
  initializePostHogRuntime();
  bindCheckoutTracking();
  bindProductCardTracking();
  bindNavigationTracking();
  bindGiftPromoTracking();
  bindPdpDisclosureTracking();
  bindPdpTrustCueTracking();
  bindCartViewTracking();
}

// Auto-initialize when DOM is ready
if (isClient()) {
  const previousReadyHandler = window[POSTHOG_READY_HANDLER_GLOBAL];
  if (typeof previousReadyHandler === 'function') {
    document.removeEventListener(POSTHOG_READY_EVENT, previousReadyHandler);
  }
  window[POSTHOG_READY_HANDLER_GLOBAL] = handlePostHogReady;
  document.addEventListener(POSTHOG_READY_EVENT, handlePostHogReady);
  if (window.__SC_POSTHOG_READY === true) {
    handlePostHogReady();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}

// Export as global for use in Shopify theme
if (isClient()) {
  window.SnowcubesAnalytics = {
    EVENTS: ANALYTICS_EVENTS,
    ENVIRONMENTS: ENVIRONMENTS,
    track: track,
    trackProduct: trackProduct,
    captureProductView: captureProductView,
    initializePostHogRuntime: initializePostHogRuntime,
    bindCheckoutTracking: bindCheckoutTracking,
    bindProductCardTracking: bindProductCardTracking,
    bindNavigationTracking: bindNavigationTracking,
    bindGiftPromoTracking: bindGiftPromoTracking,
    captureGiftPromoView: captureGiftPromoView,
    captureGiftFlowView: captureGiftFlowView,
    bindPdpDisclosureTracking: bindPdpDisclosureTracking,
    bindPdpTrustCueTracking: bindPdpTrustCueTracking,
    bindCartViewTracking: bindCartViewTracking,
    captureCartView: captureCartView,
    getSessionAttribution: getSessionAttribution,
    getRuntimeContext: getRuntimeContext,
    getReferrerHost: getReferrerHost,
    getEnvironment: getEnvironment,
    init: init,
  };
}
